import { type Ref, computed, onMounted } from 'vue';
import dayjs from 'dayjs';

import { scrollToSelectedDate, scrollToMonthOfDate } from '../lib';
import { useCalendarLazyLoader } from './calendar-lazy-loader';

interface CalendarInitialScrollOptions {
  calendarRef: Ref<HTMLElement | undefined>;
  selectedDates: string[];
  minDate: string;
  markedDates: string[];
}

function getLatestDate(dates: string[]): string {
  if (!dates.length) {
    return '';
  }
  return dates.reduce((latest, current) =>
    dayjs(current).isAfter(latest) ? current : latest,
  );
}

/**
 * Resolves the date used for the initial calendar scroll position and lazy
 * loader month count. For legs that already have a selected date this returns
 * that date (preserving the existing round-trip behaviour). For fresh
 * multi-trip legs (`selectedDates` is empty) it falls back to `minDate`
 * (= previous leg's date) or the latest marked date.
 */
export function getInitialScrollDate(
  selectedDates: string[],
  minDate: string,
  markedDates: string[],
): string {
  if (selectedDates[0]) {
    return selectedDates[0];
  }
  if (minDate) {
    return String(minDate);
  }
  return getLatestDate(markedDates);
}

export function useCalendarInitialScroll({
  calendarRef,
  selectedDates,
  minDate,
  markedDates,
}: CalendarInitialScrollOptions) {
  const scrollDate = computed(() =>
    getInitialScrollDate(selectedDates, minDate, markedDates),
  );

  const lazyLoader = useCalendarLazyLoader(scrollDate.value);

  onMounted(() => {
    if (!calendarRef.value) {
      return;
    }
    if (selectedDates[0]) {
      scrollToSelectedDate(calendarRef.value, '.js-calendar-month');
      return;
    }
    scrollToMonthOfDate(calendarRef.value, scrollDate.value);
  });

  return { lazyLoader };
}
